import { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button, FormField, Input, Select } from '@shared-ui/components';
import { PAGE_LINK_HOSTS, pageLinkSlug } from '@padcanvas/shared';
import type { Destination, PublishedLink, Publisher } from '../api/publishedLinksApi';
import {
  DESTINATION_KINDS, TARGET_LABEL, TARGET_PLACEHOLDER,
  destinationTarget, publicUrl, shortUrl, type DestinationKind,
} from './publishedLinkFormat';

/** The same URL a person would follow, tagged as a print arrival. */
export function QrPanel({ link }: { link: PublishedLink }) {
  const url = `${publicUrl(link)}?c=qr`;
  return (
    <div className="pl-qr">
      <p className="pl-sub">{url}</p>
      <QRCodeCanvas value={url} size={220} includeMargin />
      <p className="pl-sub">
        Right-click the code to save it. <code>?c=qr</code> is what tells a print arrival from a pasted one.
      </p>
    </div>
  );
}

/**
 * Create a link, or repoint / re-tag an existing one.
 *
 * On edit, only the destination's target is editable, not its kind: changing a
 * chapter link into a simulation link is a different link, and the URL is
 * already published as this one. The slug and publisher are likewise fixed.
 *
 * Validation lives on the server (`validatePublishedLink`), which reports every
 * problem at once; this form only stops an obviously empty submit.
 */
export function LinkForm({ link, saving, error, onCancel, onCreate, onSave }: {
  link: PublishedLink | null;
  saving: boolean;
  error: Error | null;
  onCancel: () => void;
  onCreate: (link: Partial<PublishedLink>) => void;
  onSave: (slug: string, changes: Partial<PublishedLink>) => void;
}) {
  const isNew = !link;
  const d = link?.destination;
  const [slug, setSlug] = useState(link?.slug ?? '');
  const [kind, setKind] = useState<DestinationKind>(d?.kind ?? 'chapter-practice');
  const [target, setTarget] = useState(d ? destinationTarget(d) : '');
  const [chapterNumber, setChapterNumber] = useState(d?.kind === 'chapter-practice' ? d.chapterNumber ?? '' : '');
  const [videoUrl, setVideoUrl] = useState(d?.kind === 'chapter-practice' ? d.videoUrl ?? '' : '');
  const [publisherKind, setPublisherKind] = useState<'partner' | 'us'>('partner');
  const [partnerId, setPartnerId] = useState('');
  const [campaign, setCampaign] = useState(link?.campaign ?? '');
  const [variant, setVariant] = useState(link?.variant ?? '');
  const [notes, setNotes] = useState(link?.notes ?? '');

  // A new link to a zitmus page is published as `<host>/<page…>/<source>`, so
  // the slug field becomes the source and the stored slug is derived.
  const zitmusPage = (() => {
    if (!isNew || kind !== 'site') return null;
    try {
      const u = new URL(target.trim());
      if (!(PAGE_LINK_HOSTS as readonly string[]).includes(u.host)) return null;
      const path = u.pathname.replace(/^\/+|\/+$/g, '');
      return path ? { origin: u.origin, path } : null;
    } catch { return null; }
  })();
  const finalSlug = zitmusPage ? pageLinkSlug(zitmusPage.path, slug.trim()) : slug.trim();

  const destination = (): Destination => {
    const t = target.trim();
    switch (kind) {
      case 'chapter-practice':
        return {
          kind, questionSetId: t,
          ...(chapterNumber.trim() ? { chapterNumber: chapterNumber.trim() } : {}),
          ...(videoUrl.trim() ? { videoUrl: videoUrl.trim() } : {}),
        };
      case 'question-set': return { kind, questionSetId: t };
      case 'simulation': return { kind, simulationId: t };
      case 'route': return { kind, path: t };
      case 'site': return { kind, url: t };
    }
  };

  const publisher = (): Publisher =>
    (publisherKind === 'us' ? { kind: 'us' } : { kind: 'partner', id: partnerId.trim() });

  const ready = !!target.trim()
    && (!isNew || (!!slug.trim() && (publisherKind === 'us' || !!partnerId.trim())));

  const submit = () => {
    if (isNew) {
      // Absent rather than empty: an optional field left blank is not set.
      onCreate({
        slug: finalSlug,
        destination: destination(),
        publisher: publisher(),
        ...(campaign.trim() ? { campaign: campaign.trim() } : {}),
        ...(variant.trim() ? { variant: variant.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      return;
    }
    onSave(link.slug, {
      destination: destination(),
      // Empty clears the field rather than storing an empty string.
      campaign: (campaign.trim() || null) as unknown as string,
      variant: (variant.trim() || null) as unknown as string,
      notes: (notes.trim() || null) as unknown as string,
    });
  };

  return (
    <form className="pl-form" onSubmit={e => { e.preventDefault(); if (ready && !saving) submit(); }}>
      {!isNew && <p className="pl-sub pl-span">The URL never changes. Repointing moves where it lands.</p>}

      <FormField label="Destination" required>
        <Select value={kind} disabled={!isNew} onChange={e => setKind(e.target.value as DestinationKind)}>
          {DESTINATION_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </Select>
      </FormField>

      <FormField label={TARGET_LABEL[kind]} required>
        <Input value={target} onChange={e => setTarget(e.target.value)} placeholder={TARGET_PLACEHOLDER[kind]} />
      </FormField>

      {isNew && (
        <FormField label={zitmusPage ? 'Source' : 'Slug'} required className="pl-span">
          <Input
            value={slug}
            onChange={e => setSlug(e.target.value.toLowerCase())}
            placeholder={zitmusPage ? 'vkdadu' : 'icse6-bio-4'}
          />
          <p className="pl-sub">
            {zitmusPage
              ? <>{shortUrl(`${zitmusPage.origin}/${zitmusPage.path}/${slug.trim() || '…'}`)} — also /p/{finalSlug || '…'}.</>
              : <>{shortUrl(publicUrl({ slug: slug.trim() || '…', destination: destination() }))}.</>}
            {' '}Permanent once published. Lowercase letters, digits and hyphens.
          </p>
        </FormField>
      )}

      {kind === 'chapter-practice' && (
        <>
          <FormField label="Chapter number">
            <Input value={chapterNumber} onChange={e => setChapterNumber(e.target.value)} placeholder="4" />
          </FormField>
          <FormField label="Video">
            <Input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtu.be/…" />
          </FormField>
        </>
      )}

      {isNew && (
        <>
          <FormField label="Publisher" required>
            <Select value={publisherKind} onChange={e => setPublisherKind(e.target.value as 'partner' | 'us')}>
              <option value="partner">Partner</option>
              <option value="us">Us</option>
            </Select>
          </FormField>
          {publisherKind === 'partner'
            ? (
              <FormField label="Partner ID" required>
                <Input value={partnerId} onChange={e => setPartnerId(e.target.value)} placeholder="narayana" />
              </FormField>
            )
            : <div />}
        </>
      )}

      <FormField label="Campaign">
        <Input value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="cell-reel-oct" />
      </FormField>

      <FormField label="Variant">
        <Input value={variant} onChange={e => setVariant(e.target.value)} placeholder="caption-a" />
      </FormField>

      <FormField label="Notes" className="pl-span">
        <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="For whoever finds this row later" />
      </FormField>

      {error && <p className="pl-error pl-span">{error.message}</p>}

      <div className="pl-form-actions pl-span">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" size="sm" disabled={!ready || saving}>
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
